import React from "react";
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import NoData from "../../../NoData";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function LineChart({
  data,
  className = "",
  isLoading = false,
  error = null,
  selectedTimezone = "America/New_York",
}) {
  const formatHourLabel = (hour) => {
    if (hour === 0) return "12 am";
    if (hour < 12) return `${hour} am`;
    if (hour === 12) return "12 pm";
    return `${hour - 12} pm`;
  };

  const formatZonedTime = (utcDate, timezoneId) => {
    const zoned = dayjs.utc(utcDate).tz(timezoneId);
    return zoned.minute() === 0 ? zoned.format("h a") : zoned.format("h:mm a");
  };

  const convertHourForSelectedTimezone = (utcHour) => convertUTCHourToSelectedTimezone(utcHour);

  const transformData = () => {
    if (data?.hourly_data) {
      return data.hourly_data.map((item) => ({
        hour: item.hour,
        hourLabel: convertHourForSelectedTimezone(item.hour),
        totalCalls: item.total_calls,
        callsConnected: item.connected_calls,
      }));
    }

    const staticHeatmapData = [
      { day_of_week: 0, hour_of_day: 18, total_calls: 8 },
      { day_of_week: 0, hour_of_day: 19, total_calls: 7 },
    ];

    const heatmapData = data?.heatmap || staticHeatmapData;
    const hourlyTotals = Array(24).fill(0);

    heatmapData.forEach(({ hour_of_day, total_calls }) => {
      hourlyTotals[hour_of_day] += total_calls;
    });

    return hourlyTotals.map((totalCalls, hour) => {
      const callsConnected = Math.round(totalCalls * (37.5 / 100)); // Default connect rate

      return {
        hour,
        hourLabel: convertHourForSelectedTimezone(hour),
        totalCalls,
        callsConnected,
      };
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="bg-gray-200 h-6 w-48 rounded mb-4"></div>
          <div className="bg-gray-200 h-64 w-full rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load chart data. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!isLoading && (!data?.hourly_data || data.hourly_data === null)) {
    return (
      <div className="bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-6">
        <NoData
          className="py-16"
          heading="No call disposition data available"
          description="Start making calls to see hourly call activity"
        />
      </div>
    );
  }
  const getClientTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return "UTC";
    }
  };

  const normalizeTimezoneAbbr = (abbr, timeZoneId) => {
    if (abbr === "GMT+5:30" || timeZoneId === "Asia/Kolkata" || timeZoneId === "Asia/Calcutta") {
      return "IST";
    }
    return abbr;
  };

  const getClientTimezoneAbbr = () => {
    try {
      const clientTimezone = getClientTimezone();
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: clientTimezone,
        timeZoneName: "short",
      });
      const parts = formatter.formatToParts(now);
      const timeZonePart = parts.find((part) => part.type === "timeZoneName");
      const abbr = timeZonePart ? timeZonePart.value : "UTC";
      return normalizeTimezoneAbbr(abbr, clientTimezone);
    } catch (e) {
      return "UTC";
    }
  };

  const getSelectedTimezoneAbbr = () => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: selectedTimezone,
        timeZoneName: "short",
      });
      const parts = formatter.formatToParts(now);
      const timeZonePart = parts.find((part) => part.type === "timeZoneName");
      const abbr = timeZonePart ? timeZonePart.value : "UTC";
      return normalizeTimezoneAbbr(abbr, selectedTimezone);
    } catch (e) {
      return "UTC";
    }
  };

  const convertUTCHourToSelectedTimezone = (utcHour) => {
    try {
      const today = new Date();
      const utcDate = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), utcHour, 0, 0)
      );

      const formatted = formatZonedTime(utcDate, selectedTimezone);
      if (formatted) return formatted;

      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: selectedTimezone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const formattedFallback = formatter.format(utcDate);
      const matchFallback = formattedFallback.match(/(\d+):?(\d{2})?\s*(AM|PM)/i);
      if (matchFallback) {
        const [, hour, minute = "00", period] = matchFallback;
        const minutePart = minute === "00" ? "" : `:${minute}`;
        return `${hour}${minutePart} ${period.toLowerCase()}`.trim();
      }

      return formatHourLabel(utcHour);
    } catch (e) {
      return formatHourLabel(utcHour);
    }
  };

  const convertUTCHourToClientTimezone = (utcHour) => {
    try {
      const clientTimezone = getClientTimezone();

      const today = new Date();
      const utcDate = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), utcHour, 0, 0)
      );

      const formatted = formatZonedTime(utcDate, clientTimezone);
      if (formatted) return formatted;

      const clientDate = new Date(utcDate.toLocaleString("en-US", { timeZone: clientTimezone }));
      const clientHour = clientDate.getHours();
      return formatHourLabel(clientHour);
    } catch (e) {
      return formatHourLabel(utcHour);
    }
  };

  const chartData = transformData();

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    const selectedTimezoneAbbr = getSelectedTimezoneAbbr();
    const clientTimezoneAbbr = getClientTimezoneAbbr();

    return (
      <div className="bg-[#0A0D12] text-white rounded-lg px-3 py-2 shadow-xl text-sm">
        <div className="space-y-0">
          <div className="flex gap-2 pb-2 ">
            <div className="flex items-center gap-1">
              <span className="text-white font-semibold text-xs">{selectedTimezoneAbbr}:</span>
              <span className="font-semibold text-white text-xs">
                {convertUTCHourToSelectedTimezone(data.hour)}
              </span>
            </div>
            <span className="text-white">•</span>
            <div className="flex items-center gap-1">
              <span className="text-white font-semibold text-xs">{clientTimezoneAbbr}:</span>
              <span className="text-white font-semibold text-xs">
                {convertUTCHourToClientTimezone(data.hour)}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[#7F56D9] text-xl leading-none">•</span>
              <span>
                Calls made: <span className="font-semibold">{data.totalCalls}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#B692F6] text-xl leading-none">•</span>
              <span>
                Calls connected: <span className="font-semibold">{data.callsConnected}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CustomLegend = () => (
    <div className="flex items-center justify-end gap-6 mb-2">
      <div className="flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-[#7F56D9]" />
        <span className="text-xs text-gray-600">Total calls made</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-2.5 h-2.5 rounded-full bg-[#B692F6]" />
        <span className="text-xs text-gray-600">Calls connected</span>
      </div>
    </div>
  );

  return (
    <div className={`bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-6 ${className}`}>
      <CustomLegend />

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: -20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />

          <XAxis
            dataKey="hourLabel"
            tick={{ fontSize: 11, fill: "#6B7280" }}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
            interval={1}
          />

          <YAxis
            label={{
              value: "Number of calls",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11, fill: "#6B7280", textAnchor: "middle" },
            }}
            tick={{ fontSize: 11, fill: "#6B7280" }}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#818CF8", strokeWidth: 1 }} />

          <Bar dataKey="totalCalls" fill="url(#barGradient)" barSize={8} radius={[2, 2, 0, 0]} />

          <Area
            type="monotone"
            dataKey="totalCalls"
            stroke="none"
            fill="url(#totalAreaGradient)"
            fillOpacity={1}
          />

          <Area
            type="monotone"
            dataKey="callsConnected"
            stroke="none"
            fill="url(#connectedAreaGradient)"
            fillOpacity={1}
          />

          <Line
            type="monotone"
            dataKey="totalCalls"
            stroke="#7F56D9"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "#E9EAEB" }}
          />

          <Line
            type="monotone"
            dataKey="callsConnected"
            stroke="#B692F6"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "#9E77ED" }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
